import { makeError } from '@mimir/common/util/exception';
import type { Fraction } from '@mimir/common/util/fractionType';
import { approximateFractionValue, divideFractionBy, formatFractionAsExpression, simplifyFraction, subtractFractions} from '@mimir/common/util/integerMath';
import { BufferCoordinator } from './sourceBufferCoordinator';
import { TaskTracking } from '@mimir/common/async/taskTracking';
import type { BufferingContext } from './fetching';
import type { StreamCandidate } from './streamCandidate';
import { makeStreamCandidates } from './streamCandidate';
import type { BufferingParameters} from './bufferingTasks';
import { BufferTaskSearch } from './bufferingTasks';
import type { BufferingResult, NextTask } from './tasks';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { CodecString } from '@mimir/common/dto/mediaAvailability';
import { isVideoStreamRole } from '@mimir/common/dto/mediaDtos';
import { BufferBlob, Offset } from '@mimir/common/io/blobView';
import { findTrackBaseMediaDecodeTime, findVideoTrackTimescale } from '@mimir/common/format/mp4Box';
import { sameAudioTransforms, type AudioTransform } from './audioTransform';

function createSourceBuffer(ms: MediaSource, codec: CodecString) {
  try {
    return ms.addSourceBuffer(codec);
  } catch (cause) {
    throw makeError('Browser can not make decode buffer for ' + codec, 'MseError', { cause, info: { codec } });
  }
}

interface SampleTime {
  trackId: number;
  segmentTime: number;
  time: Fraction;
}

/**
 * Knows how to obtain media segments and buffer them for an HtmlVideoElement.
 * Has a URL that can be assigned as the src attribute of a video element to
 * make the video element use media that has been buffered.
 */
export class StreamingMediaSource {
  private readonly mediaSource: MediaSource;
  private primary: StreamRoleId | null =  null;
  public debug: string = '';
  private ongoingBufferingTasks = new TaskTracking<BufferingResult>();
  private earliest: SampleTime | null = null;
  private latest: SampleTime | null = null;
  private lastWarningEnd = 0;
  private audioTransform: AudioTransform;
  private url : string;

  private buffers: Partial<Record<StreamRoleId, BufferCoordinator>> = {};

  constructor(
    codecsByStreamId: Partial<Record<StreamRoleId, CodecString>>,
    streamSet: StreamRoleId[],
    onSourceOpen: () => void,
    useFlacDownmix: boolean,
  ) {
    this.audioTransform = useFlacDownmix
      ? {
        type: 'flacDownmix',
        mixMatrix: [],
      }
      : {
        type: 'solo',
        audioTrackIndex: 0,
      };
    const ms = new MediaSource();
    this.url = URL.createObjectURL(ms);

    const onOpen = () => {
      /*
        By removing the event listener after receiving the first event we
        prevent subsequent events caused by the readyState transition from
        "ended" to "open" to duplicate buffers.
      */
      ms.removeEventListener('sourceopen', onOpen);

      for (const streamId of streamSet) {
        // The original codec string is the one describing the media in the .ts segments.
        // For the MSE to work, we need to give it a string describing what it will
        // actually have to decode from the buffers we feed it with, which will be mp4,
        // possibly with flac instead of aac
        const originalCodec = codecsByStreamId[streamId];
        let codec = originalCodec;
        switch(codec) {
          case CodecString.MPEGTS: {
            codec = useFlacDownmix ? CodecString.MP4_VIDEO_WITH_FLAC : CodecString.MP4_VIDEO_WITH_AUDIO;
            break;
          }
          case CodecString.TS_VIDEO_H264_BASELINE_AVC_20: {
            codec = CodecString.MP4_VIDEO_H264_BASELINE_AVC_20;
            break;
          }
          case CodecString.MPEGTS_AUDIO_ONLY: {
            codec = CodecString.MP4_AUDIO_FLAC;
            break;
          }
        }

        if (codec) {
          if (this.primary == null || isVideoStreamRole(streamId)) {
            this.primary = streamId;
          }

          this.buffers[streamId] = new BufferCoordinator(
            createSourceBuffer(ms, codec)
          );
        }
      }

      onSourceOpen();
    };
    ms.addEventListener('sourceopen', onOpen);

    this.mediaSource = ms;
  }

  public getUrl(): string {
    return this.url;
  }

  async setAudioTransform(audioTransform: AudioTransform) {
    if (sameAudioTransforms(this.audioTransform, audioTransform)) {
      // We don't want to evict data unless there are some actual differences
      // in the audio transform to apply
      return;
    }

    this.audioTransform = audioTransform;
    for (const coordinator of Object.values(this.buffers)) {
      try {
        await coordinator.evictAll();
      } catch (err) {
        console.trace('Error while evicting data', err);
      }
    }
  }

  close() {
    window.URL.revokeObjectURL(this.url);
  }

  /**
   * Check whether all of the buffers have buffered the final segment, and if so
   * signal end of stream.
   */
  checkAllEnded() {
    const bufs = Object.values(this.buffers) as BufferCoordinator[];
    if (bufs.length && bufs.every(b => b.hasEnded && !b.buffer.updating)) {
      this.mediaSource.endOfStream();
    }
  }

  nextBufferTask(
    context: BufferingContext,
    params: BufferingParameters,
    segmentDuration: Fraction,
  ): NextTask {
    if (this.mediaSource.readyState == 'closed') {
      return { blocker: 'Media source is closed' };
    }

    const effectiveSegmentDuration = this.calculateSegmentDuration() ?? segmentDuration;

    const streamCandidates: StreamCandidate[] = makeStreamCandidates(this.buffers, effectiveSegmentDuration);
    const finder = new BufferTaskSearch(context, this.ongoingBufferingTasks, effectiveSegmentDuration, this.audioTransform);
    const next = finder.findNextBufferingTask(streamCandidates, params);
    const innerTask = next.task;
    if (innerTask) {
      /*
        Wrap task to check whether to signal end of stream as a result of the
        buffering.
      */
      next.task = async () => {
        const result = await innerTask();
        this.checkAllEnded();
        if (result.streamId == this.primary) {
          await this.rememberTimestamps(result, segmentDuration);
        }

        return result;
      };
    }
    return next;
  }

  async rememberTimestamps(result: BufferingResult, segmentDuration: Fraction) {
    let initInfo: { trackId: number, timescale: number } | null  = null;
    const init = result.buffers.initialization;
    if (init && ArrayBuffer.isView(init)) {
      const blob = new BufferBlob(init.buffer, new Offset(null, init.byteOffset));
      initInfo = await findVideoTrackTimescale(blob);
    }

    const seg = result.buffers.mediaSegment;
    let time: Fraction | null = null;
    if (initInfo && seg && ArrayBuffer.isView(seg)) {
      const blob = new BufferBlob(seg.buffer, new Offset(null, seg.byteOffset));
      const baseMediaDecodeTime = await findTrackBaseMediaDecodeTime(blob, initInfo.trackId);
      if (baseMediaDecodeTime) {
        time = simplifyFraction({ numerator: Number(baseMediaDecodeTime), denominator: initInfo.timescale });
        const sampleTime: SampleTime = {
          trackId: initInfo.trackId,
          segmentTime: result.segmentTime,
          time
        };

        if (this.earliest == null || this.earliest.segmentTime > sampleTime.segmentTime) {
          this.earliest = sampleTime;
        }

        if (this.latest == null || this.latest.segmentTime < sampleTime.segmentTime) {
          this.latest = sampleTime;
        }
      }
    }

    const calcSegmentDuration = this.calculateSegmentDuration();
    if (calcSegmentDuration) {
      this.debug = result.streamId + ' segTime ' + result.segmentTime + ', reported segDuration ' + formatFractionAsExpression(segmentDuration) + ' (' + approximateFractionValue(segmentDuration) + '), calculated segDuration ' + formatFractionAsExpression(calcSegmentDuration) + ' (' + approximateFractionValue(calcSegmentDuration) + ')';
    } else {
      this.debug = 'Cannot calculate segment time yet.';
    }
  }

  calculateSegmentDuration() {
    if (this.earliest && this.latest && this.earliest != this.latest) {
      const timeSpan = subtractFractions(this.latest.time, this.earliest.time);
      return divideFractionBy(timeSpan, this.latest.segmentTime - this.earliest.segmentTime);
    }
    return null;
  }

  expandSeekableEnd(seekableEnd: number) {
    /*
      According to the Media Source Extensions specification, just calling
      setLiveSeekableRange() should not invalidate any of the decode buffers.

      However, at least Chrome at 2021-Sep-06 does invalidate the parts of the
      decode buffers that are outside of the seek range.

      There is a Chrome ticket tracking this discrepancy:
      https://bugs.chromium.org/p/chromium/issues/detail?id=623729

      As a result, we'll need to take special care not to incorrectly invalidate
      buffered segments in situations in which the segment timestamps doesn't
      exactly match our expectations.
    */
    if (this.mediaSource.readyState != 'open') {
      console.warn(`expandSeekableEnd was blocked because the mediaSource ready state was: ${this.mediaSource.readyState}`);
      return;
    }

    for (const sourceBuffer of this.mediaSource.sourceBuffers) {
      if (sourceBuffer.updating) {
        console.warn('expandSeekableEnd was blocked by one or more of the source buffers being in an updating state');
        return;
      }
    }

    try {
      const bufferedEnd = getEndOfBufferedMedia(this.mediaSource);
      if (bufferedEnd > seekableEnd) {
        if (this.lastWarningEnd != seekableEnd) {
          this.lastWarningEnd = seekableEnd;
          console.warn('Adjusting seekableEnd to avoid clipping decode buffer', { seekableEnd, bufferedEnd, delta: bufferedEnd - seekableEnd });
        }
        seekableEnd = bufferedEnd;
      }

      this.mediaSource.setLiveSeekableRange(0, seekableEnd);
    } catch (ex) {
      throw makeError('Unable to set video live seekable range', 'MediaStateError');
    }

    try {
      this.mediaSource.duration = seekableEnd;
    } catch (ex) {
      throw makeError('Unable to set duration', 'MediaStateError');
    }

  }
}

function getEndOfBufferedMedia(ms: MediaSource) {
  const activeBufs = ms.activeSourceBuffers;
  let bufferedEnd = 0;
  for (let i = 0; i < activeBufs.length; ++i) {
    const buf = activeBufs[i];
    const buffered = buf.buffered;
    if (buffered.length > 0) {
      bufferedEnd = Math.max(bufferedEnd, buffered.end(buffered.length - 1));
    }
  }

  return bufferedEnd;
}
